import { useState } from 'react';

import { YStack } from '@/components/ui/layout/layout';
import { SaveableField } from '@/components/ui/SaveableField';
import { SettingsSection } from '@/features/settings/settings-section';
import { cn } from '@/lib/utils';

import { teamApis } from '../team.query';
import { TEAM_CUSTOM_INSTRUCTIONS_MAX_LENGTH } from '../team.types';

type TeamCustomInstructionsCardProps = {
  teamId: string;
  instructions: string;
};

export const TeamCustomInstructionsCard = ({
  teamId,
  instructions,
}: TeamCustomInstructionsCardProps) => {
  const [draft, setDraft] = useState(instructions);
  const [saving, setSaving] = useState(false);
  const tooLong = draft.length > TEAM_CUSTOM_INSTRUCTIONS_MAX_LENGTH;

  const save = async () => {
    setSaving(true);
    try {
      await teamApis.updateDataControls(teamId, {
        custom_instructions: draft,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <YStack className="w-full gap-3">
      <SettingsSection.GroupHeader>
        Team Instructions
      </SettingsSection.GroupHeader>
      <SettingsSection.Group>
        <SettingsSection.Item direction="vertical">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemDescription>
              Every member's assistant follows these instructions, such as
              company policy, tone and key facts. A member's own custom
              instructions take priority. Members can't see this text.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <SaveableField
            id="team-custom-instructions"
            className="text-sm resize-none min-h-32"
            placeholder="Enter instructions for your team's assistants"
            value={draft}
            dirty={draft !== instructions}
            saveDisabled={tooLong}
            busy={saving}
            onChange={setDraft}
            onSave={() => void save()}
            onDiscard={() => setDraft(instructions)}
            statusContent={
              <span
                className={cn(
                  'text-xs',
                  tooLong ? 'text-danger-11' : 'text-foreground-muted'
                )}
              >
                {draft.length} / {TEAM_CUSTOM_INSTRUCTIONS_MAX_LENGTH}
              </span>
            }
          />
        </SettingsSection.Item>
      </SettingsSection.Group>
    </YStack>
  );
};
